import React from 'react';
import classnames from 'classnames';
import { Icon } from '../icon';
import { CommonInputProps } from '../types';
import { InputMessage } from '../input-message';

import styles from './select.module.scss';

const isOptionGroup = (optionProps: unknown): optionProps is GroupOptionType =>
  typeof (optionProps as GroupOptionType)?.optgroupLabel === 'string';

export type OptionType = { label: string; value: string };
export type GroupOptionType = { optgroupLabel: string; options: OptionType[] };

export type SelectProps = {
  options: (GroupOptionType | OptionType)[];
  value: string;
} & CommonInputProps &
  Pick<React.InputHTMLAttributes<HTMLSelectElement>, 'autoFocus' | 'onChange' | 'autoComplete'>;

const baseClass = 'select';
const innerClass = `${baseClass}__inner`;

const renderOptions = (options: (GroupOptionType | OptionType)[]) =>
  options.map((optionObj, i) =>
    isOptionGroup(optionObj) ? (
      <optgroup
        key={optionObj.optgroupLabel + i}
        label={optionObj.optgroupLabel}
        className={styles[`${innerClass}__optgroup`]}
      >
        {optionObj.options.map((option, index) => (
          <option key={option.value + index} value={option.value} className={styles[`${innerClass}__option`]}>
            {option.label}
          </option>
        ))}
      </optgroup>
    ) : (
      <option key={optionObj.value + i} value={optionObj.value} className={styles[`${innerClass}__option`]}>
        {optionObj.label}
      </option>
    )
  );

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      disabled,
      label,
      id,
      value,
      required,
      message,
      isInvalid,
      name,
      onChange,
      options,
      autoFocus,
      autoComplete
    },
    ref
  ) => {
    const outerClasses = classnames(baseClass, className);
    const classes = classnames(
      styles[`${baseClass}__inner`],
      { [styles[`${innerClass}--has-value`]]: Boolean(value) },
      { [styles[`${innerClass}--error`]]: isInvalid }
    );
    const labelId = `${id}_label`;
    const messageId = `${id}__message`;

    return (
      <div className={outerClasses} data-testid="select-component">
        <div className={classes}>
          <select
            ref={ref}
            disabled={disabled}
            id={id}
            value={value}
            name={name}
            required={required}
            aria-labelledby={labelId}
            aria-describedby={isInvalid ? messageId : undefined}
            aria-invalid={Boolean(isInvalid)}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            onChange={onChange}
            className={styles[`${innerClass}__element`]}
          >
            {renderOptions(options)}
          </select>
          <label htmlFor={id} id={labelId} className={styles[`${innerClass}__label`]}>
            {label}
          </label>
          <Icon name="chevron-down--small" className={styles[`${innerClass}__icon`]} aria-hidden />
        </div>
        {message && (
          <InputMessage
            isError={isInvalid}
            message={message}
            disabled={disabled}
            className={styles[`${baseClass}__input-message`]}
            id={messageId}
          />
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
